import { useEffect, useState, useRef } from 'react'
import axios from 'axios'
import api from '../services/api'

interface LogItem {
  source: string
  id: string
  title: string
  count: number
  model?: string
  mtime: number
  num?: number
}

interface Block {
  kind: 'text' | 'command' | 'tool' | 'result'
  text?: string
  code?: string
  desc?: string
  tool?: string
  input?: string
}

interface Turn { role: string; blocks: Block[] }

const SOURCE: Record<string, { label: string; color: string; icon: string; bubble: string; border: string }> = {
  claude: { label: 'Claude', color: '#ec4899', icon: '🤖', bubble: '#1a1024', border: '#3b0764' },
  goose:  { label: 'Goose',  color: '#14b8a6', icon: '🦆', bubble: '#0a1f1c', border: '#134e4a' },
}
const src = (s: string) => SOURCE[s] || SOURCE.claude

// Instance dédiée : envoie le token Hub MAIS ne déclenche PAS le logout global sur 401
const cliApi = axios.create({ baseURL: (api as any).defaults.baseURL })
cliApi.interceptors.request.use((cfg) => {
  const hub = localStorage.getItem('claudie_token')
  if (hub) cfg.headers.Authorization = `Bearer ${hub}`
  return cfg
})

function formatDate(mtime: number): string {
  try {
    return new Date(mtime * 1000).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch { return '' }
}

function CopyBtn({ text }: { text: string }) {
  const [ok, setOk] = useState(false)
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text).then(() => { setOk(true); setTimeout(() => setOk(false), 1200) }) }}
      style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer',
        background: ok ? '#065f46' : '#374151', color: ok ? '#34d399' : '#d1d5db', border: 'none' }}
    >{ok ? '✓ copié' : 'copier'}</button>
  )
}

function BlockView({ b }: { b: Block }) {
  if (b.kind === 'text') {
    return <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55 }}>{b.text}</div>
  }
  if (b.kind === 'command') {
    return (
      <div style={{ margin: '6px 0' }}>
        {b.desc && <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>{b.desc}</div>}
        <div style={{ background: '#0b0f17', border: '1px solid #1f2937', borderRadius: '6px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', borderBottom: '1px solid #1f2937' }}>
            <span style={{ fontSize: '11px', color: '#6b7280' }}>$ {b.tool}</span>
            <CopyBtn text={b.code || ''} />
          </div>
          <pre style={{ margin: 0, padding: '8px 10px', overflowX: 'auto', color: '#a5f3fc', fontSize: '12.5px', fontFamily: 'monospace' }}>{b.code}</pre>
        </div>
      </div>
    )
  }
  if (b.kind === 'tool') {
    return (
      <div style={{ margin: '6px 0', background: '#0b0f17', border: '1px solid #1f2937', borderRadius: '6px' }}>
        <div style={{ padding: '4px 8px', fontSize: '11px', color: '#c084fc', borderBottom: '1px solid #1f2937' }}>🔧 {b.tool}</div>
        <pre style={{ margin: 0, padding: '8px 10px', overflowX: 'auto', color: '#9ca3af', fontSize: '12px', fontFamily: 'monospace' }}>{b.input}</pre>
      </div>
    )
  }
  return (
    <details style={{ margin: '6px 0' }}>
      <summary style={{ cursor: 'pointer', fontSize: '11px', color: '#6b7280' }}>↳ résultat outil</summary>
      <pre style={{ margin: '4px 0 0', padding: '8px 10px', maxHeight: '260px', overflow: 'auto', background: '#0b0f17', border: '1px solid #1f2937', borderRadius: '6px', color: '#9ca3af', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{b.text}</pre>
    </details>
  )
}

export default function SessionsPage() {
  // Verrou dédié (jeton en mémoire seule) + 2FA email
  const [cliToken, setCliToken] = useState<string | null>(null)
  const [step, setStep] = useState<'password' | 'code'>('password')
  const [pw, setPw] = useState('')
  const [code, setCode] = useState('')
  const [maskedEmail, setMaskedEmail] = useState('')
  const [unlocking, setUnlocking] = useState(false)
  const [unlockError, setUnlockError] = useState('')

  const [logs, setLogs] = useState<LogItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<'all' | 'claude' | 'goose'>('all')
  const [gotoNum, setGotoNum] = useState('')
  const [selected, setSelected] = useState<LogItem | null>(null)
  const [turns, setTurns] = useState<Turn[]>([])
  const [loadingConv, setLoadingConv] = useState(false)
  const [convError, setConvError] = useState('')
  const convRef = useRef<HTMLDivElement>(null)

  const relock = (msg: string) => {
    sessionStorage.removeItem('cli_token')
    setCliToken(null); setStep('password'); setCode(''); setLogs([]); setSelected(null); setTurns([]); setUnlockError(msg)
  }

  const finishUnlock = (tok: string) => {
    sessionStorage.setItem('cli_token', tok)
    setCliToken(tok); setPw(''); setCode(''); setStep('password'); fetchLogs(tok)
  }

  // Étape 1 : mot de passe → demande d'un code email (repli mdp-seul si SMTP absent)
  const submitPassword = async () => {
    setUnlocking(true); setUnlockError('')
    try {
      const res = await cliApi.post('/api/cli-logs/request-code', { password: pw })
      setMaskedEmail(res.data?.email || ''); setStep('code')
    } catch (e: any) {
      const s = e?.response?.status
      if (s === 503) {
        // SMTP pas encore configuré → repli mot de passe seul
        try {
          const r2 = await cliApi.post('/api/cli-logs/unlock', { password: pw })
          finishUnlock(r2.data?.cli_token)
        } catch (e2: any) {
          setUnlockError(e2?.response?.status === 401 ? 'Code d’accès incorrect.' : 'Erreur de déverrouillage.')
        }
      } else {
        setUnlockError(s === 401 ? 'Code d’accès incorrect.' : s === 429 ? 'Un code vient d’être envoyé, patiente un peu.' : s === 502 ? 'Échec d’envoi de l’email.' : 'Erreur.')
      }
    } finally { setUnlocking(false) }
  }

  // Étape 2 : validation du code email
  const submitCode = async () => {
    setUnlocking(true); setUnlockError('')
    try {
      const res = await cliApi.post('/api/cli-logs/verify-code', { code })
      finishUnlock(res.data?.cli_token)
    } catch (e: any) {
      const s = e?.response?.status
      setUnlockError(s === 401 ? 'Code incorrect ou expiré.' : s === 429 ? 'Trop d’essais, redemande un code.' : 'Erreur.')
    } finally { setUnlocking(false) }
  }

  const fetchLogs = async (tok?: string, silent = false) => {
    const t = tok || cliToken
    if (!t) return
    if (!silent) setLoading(true)
    setError('')
    try {
      const res = await cliApi.get('/api/cli-logs', { headers: { 'X-CLI-Token': t } })
      setLogs(res.data || [])
    } catch (e: any) {
      if (e?.response?.status === 401) return relock('Session conversations expirée — re-saisis le code.')
      if (!silent) setError('Impossible de charger la liste des conversations.')
    } finally { if (!silent) setLoading(false) }
  }

  const renameConv = async () => {
    if (!cliToken || !selected) return
    const t = window.prompt('Titre de la conversation (vide = titre automatique) :', selected.title)
    if (t === null) return
    try {
      const res = await cliApi.put(`/api/cli-logs/${selected.source}/${encodeURIComponent(selected.id)}/title`, { title: t }, { headers: { 'X-CLI-Token': cliToken } })
      if (res.data?.title) setSelected({ ...selected, title: res.data.title })
      fetchLogs(cliToken, true)
    } catch (e: any) {
      if (e?.response?.status === 401) return relock('Session conversations expirée — re-saisis le code.')
      setError('Impossible de renommer la conversation.')
    }
  }

  const jumpToNum = () => {
    const n = parseInt(gotoNum, 10)
    if (!n) return
    const hit = logs.find(l => l.num === n)
    if (hit) { setFilter('all'); openConv(hit); setGotoNum('') }
    else setError(`Aucune session #${n}.`)
  }

  const openConv = async (item: LogItem) => {
    if (!cliToken) return
    setSelected(item); setConvError(''); setLoadingConv(true); setTurns([])
    try {
      const res = await cliApi.get(`/api/cli-logs/${item.source}/${encodeURIComponent(item.id)}`, { headers: { 'X-CLI-Token': cliToken } })
      setTurns(res.data?.turns || [])
    } catch (e: any) {
      if (e?.response?.status === 401) return relock('Session conversations expirée — re-saisis le code.')
      setConvError('Impossible de charger cette conversation.')
    } finally { setLoadingConv(false) }
  }

  // Au montage : reprendre une session déverrouillée (survit au refresh, pas à la fermeture d'onglet)
  useEffect(() => {
    const saved = sessionStorage.getItem('cli_token')
    if (saved) { setCliToken(saved); fetchLogs(saved); return }
    // Zone ouverte (login Hub seul) : on saute l'écran de code dédié.
    cliApi.get('/api/cli-logs/status')
      .then(r => { if (r.data?.open) { setCliToken('open'); fetchLogs('open') } })
      .catch(() => { /* zone protégée : on garde l'écran de code */ })
  }, [])

  // Temps réel : rafraîchir la conversation ouverte + la liste toutes les 4 s
  useEffect(() => {
    if (!cliToken || !selected) return
    let stop = false
    const sig = (ts: Turn[]) => ts.length + ':' + JSON.stringify(ts[ts.length - 1] || '').length
    const tick = async () => {
      try {
        const el = convRef.current
        const nearBottom = el ? (el.scrollHeight - el.scrollTop - el.clientHeight < 80) : true
        const res = await cliApi.get(`/api/cli-logs/${selected.source}/${encodeURIComponent(selected.id)}`, { headers: { 'X-CLI-Token': cliToken } })
        if (stop) return
        const nt: Turn[] = res.data?.turns || []
        setTurns(prev => (sig(prev) === sig(nt) ? prev : nt))
        if (nearBottom) requestAnimationFrame(() => { const e = convRef.current; if (e) e.scrollTop = e.scrollHeight })
      } catch { /* silencieux */ }
      fetchLogs(cliToken, true)
    }
    const id = setInterval(tick, 4000)
    return () => { stop = true; clearInterval(id) }
  }, [cliToken, selected])

  // ----------------- Écran de verrouillage (2FA) -----------------
  if (!cliToken) {
    const inputStyle = { width: '100%', boxSizing: 'border-box' as const, padding: '10px 12px', borderRadius: '8px', background: '#111827', border: '1px solid #4b5563', color: '#e5e7eb', fontSize: '14px', marginBottom: '12px', outline: 'none' }
    const btnStyle = (disabled: boolean) => ({ width: '100%', padding: '10px', borderRadius: '8px', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', background: '#ec4899', color: '#fff', fontWeight: 600, opacity: disabled ? 0.6 : 1 })
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#111827' }}>
        <div style={{ width: '360px', background: '#1f2937', border: '1px solid #374151', borderRadius: '12px', padding: '28px', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>🔒</div>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#f3f4f6', marginBottom: '6px' }}>Conversations CLI — zone protégée</div>

          {step === 'password' && (
            <>
              <div style={{ fontSize: '12.5px', color: '#9ca3af', marginBottom: '18px', lineHeight: 1.5 }}>
                Contenu sensible. Saisis le <b>code d'accès dédié</b> (distinct du login Hub).
              </div>
              <input type="password" value={pw} autoFocus placeholder="Code d'accès"
                onChange={(e) => setPw(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && pw) submitPassword() }}
                style={inputStyle} />
              {unlockError && <div style={{ color: '#fca5a5', fontSize: '12.5px', marginBottom: '12px' }}>{unlockError}</div>}
              <button onClick={submitPassword} disabled={!pw || unlocking} style={btnStyle(!pw || unlocking)}>
                {unlocking ? 'Vérification…' : 'Continuer'}
              </button>
            </>
          )}

          {step === 'code' && (
            <>
              <div style={{ fontSize: '12.5px', color: '#9ca3af', marginBottom: '18px', lineHeight: 1.5 }}>
                Un code à 6 chiffres a été envoyé à <b>{maskedEmail}</b>. Saisis-le ci-dessous.
              </div>
              <input type="text" value={code} autoFocus placeholder="Code à 6 chiffres" inputMode="numeric" maxLength={6}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter' && code) submitCode() }}
                style={{ ...inputStyle, letterSpacing: '4px', textAlign: 'center', fontSize: '18px' }} />
              {unlockError && <div style={{ color: '#fca5a5', fontSize: '12.5px', marginBottom: '12px' }}>{unlockError}</div>}
              <button onClick={submitCode} disabled={!code || unlocking} style={btnStyle(!code || unlocking)}>
                {unlocking ? 'Validation…' : 'Valider'}
              </button>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px' }}>
                <button onClick={() => { setStep('password'); setUnlockError(''); setCode('') }} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '11px', cursor: 'pointer' }}>← mot de passe</button>
                <button onClick={submitPassword} disabled={unlocking} style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: '11px', cursor: 'pointer' }}>renvoyer un code</button>
              </div>
            </>
          )}

          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '14px' }}>Le déverrouillage expire au rafraîchissement (jeton en mémoire seule).</div>
        </div>
      </div>
    )
  }

  // ----------------- Vue déverrouillée -----------------
  const visible = logs.filter(l => filter === 'all' || l.source === filter)
  const nClaude = logs.filter(l => l.source === 'claude').length
  const nGoose = logs.filter(l => l.source === 'goose').length
  const selColor = selected ? src(selected.source).color : '#ec4899'

  const FilterBtn = ({ v, label }: { v: 'all' | 'claude' | 'goose'; label: string }) => (
    <button onClick={() => setFilter(v)}
      style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '999px', cursor: 'pointer', border: 'none',
        background: filter === v ? (v === 'goose' ? SOURCE.goose.color : v === 'claude' ? SOURCE.claude.color : '#6b7280') : '#1f2937',
        color: filter === v ? '#fff' : '#9ca3af' }}>{label}</button>
  )

  return (
    <div style={{ display: 'flex', height: '100%', backgroundColor: '#111827', color: '#e5e7eb' }}>
      {/* Liste */}
      <div style={{ width: '350px', borderRight: '1px solid #374151', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #374151' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ec4899' }}>💬 Sessions CLI 🔓</div>
            <button onClick={() => fetchLogs()} title="Rafraîchir" style={{ background: 'transparent', border: '1px solid #374151', color: '#d1d5db', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer' }}>↻</button>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <FilterBtn v="all" label={`Tout (${logs.length})`} />
            <FilterBtn v="claude" label={`🤖 Claude (${nClaude})`} />
            <FilterBtn v="goose" label={`🦆 Goose (${nGoose})`} />
            <input value={gotoNum} placeholder="# n°" inputMode="numeric"
              onChange={(e) => setGotoNum(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter') jumpToNum() }}
              title="Aller à la session n°… (Entrée)"
              style={{ marginLeft: 'auto', width: '58px', fontSize: '11px', padding: '3px 8px', borderRadius: '999px', background: '#111827', border: '1px solid #374151', color: '#e5e7eb', outline: 'none' }} />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && <div style={{ padding: '16px', color: '#9ca3af' }}>Chargement…</div>}
          {error && <div style={{ padding: '16px', color: '#fca5a5' }}>{error}</div>}
          {!loading && !error && visible.length === 0 && <div style={{ padding: '16px', color: '#9ca3af' }}>Aucune conversation.</div>}
          {visible.map((l) => {
            const active = selected?.id === l.id && selected?.source === l.source
            const c = src(l.source)
            return (
              <button key={`${l.source}:${l.id}`} onClick={() => openConv(l)}
                style={{ width: '100%', textAlign: 'left', padding: '10px 14px',
                  background: active ? '#1f2937' : 'transparent',
                  borderLeft: `3px solid ${active ? c.color : 'transparent'}`,
                  borderTop: 'none', borderRight: 'none', borderBottom: '1px solid #1f2937',
                  color: active ? '#fff' : '#d1d5db', cursor: 'pointer' }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#1a2130' }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                  {l.num != null && <span style={{ fontSize: '11px', fontWeight: 800, padding: '1px 6px', borderRadius: '6px', background: '#374151', color: '#f9fafb', whiteSpace: 'nowrap' }}>#{l.num}</span>}
                  <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '999px', background: c.color + '22', color: c.color, whiteSpace: 'nowrap' }}>{c.icon} {c.label}</span>
                  <span style={{ fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.title}</span>
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280', display: 'flex', gap: '8px' }}>
                  <span>{formatDate(l.mtime)}</span>
                  <span>· {l.count} msg</span>
                  {l.model && l.source === 'goose' && <span style={{ color: c.color + 'cc' }}>· {l.model}</span>}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Conversation */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
        {!selected && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', flexDirection: 'column', gap: '8px' }}>
            <div style={{ fontSize: '48px' }}>💬</div>
            <div>Sélectionne une conversation pour la lire</div>
          </div>
        )}
        {selected && (
          <div ref={convRef} className="conv-scroll" style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', scrollbarWidth: 'thin', scrollbarColor: '#4b5563 #0b0f17' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', paddingBottom: '10px', borderBottom: '1px solid #374151' }}>
              {selected.num != null && <span style={{ fontSize: '12px', fontWeight: 800, padding: '2px 8px', borderRadius: '6px', background: '#374151', color: '#f9fafb', whiteSpace: 'nowrap' }}>#{selected.num}</span>}
              <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', background: selColor + '22', color: selColor }}>{src(selected.source).icon} {src(selected.source).label}</span>
              <span style={{ fontSize: '13px', color: '#d1d5db', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.title}</span>
              <button onClick={renameConv} title="Renommer cette conversation" style={{ background: 'transparent', border: '1px solid #374151', borderRadius: '6px', color: '#9ca3af', cursor: 'pointer', fontSize: '12px', padding: '1px 6px' }}>✏️</button>
              <span style={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', marginLeft: 'auto' }}>{formatDate(selected.mtime)}</span>
            </div>
            {loadingConv && <div style={{ color: '#9ca3af' }}>Chargement de la conversation…</div>}
            {convError && <div style={{ color: '#fca5a5' }}>{convError}</div>}
            {!loadingConv && !convError && turns.map((t, i) => {
              const isUser = t.role === 'user'
              const c = src(selected.source)
              return (
                <div key={i} style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: isUser ? '#60a5fa' : c.color, marginBottom: '4px' }}>
                    {isUser ? '👤 Toi' : `${c.icon} ${c.label}`}
                  </div>
                  <div style={{ background: isUser ? '#0f1830' : c.bubble, border: `1px solid ${isUser ? '#1e293b' : c.border}`, borderRadius: '8px', padding: '10px 14px', fontSize: '13.5px' }}>
                    {t.blocks.map((b, j) => <BlockView key={j} b={b} />)}
                  </div>
                </div>
              )
            })}
            {!loadingConv && !convError && turns.length === 0 && <div style={{ color: '#6b7280' }}>Conversation vide.</div>}
          </div>
        )}
        {selected && (
          <div style={{ position: 'absolute', right: 20, bottom: 20, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 20 }}>
            <button onClick={() => convRef.current?.scrollTo({ top: 0, behavior: 'smooth' })} title="Aller en haut"
              style={{ width: 40, height: 40, borderRadius: '50%', border: '1px solid #374151', background: '#1f2937', color: '#e5e7eb', cursor: 'pointer', fontSize: 18, boxShadow: '0 2px 8px rgba(0,0,0,.4)' }}>↑</button>
            <button onClick={() => convRef.current?.scrollTo({ top: convRef.current.scrollHeight, behavior: 'smooth' })} title="Aller à la fin"
              style={{ width: 40, height: 40, borderRadius: '50%', border: '1px solid #374151', background: '#ec4899', color: '#fff', cursor: 'pointer', fontSize: 18, boxShadow: '0 2px 8px rgba(0,0,0,.4)' }}>↓</button>
          </div>
        )}
        <style>{`.conv-scroll::-webkit-scrollbar{width:12px}.conv-scroll::-webkit-scrollbar-track{background:#0b0f17}.conv-scroll::-webkit-scrollbar-thumb{background:#4b5563;border-radius:6px;border:2px solid #0b0f17}.conv-scroll::-webkit-scrollbar-thumb:hover{background:#6b7280}`}</style>
      </div>
    </div>
  )
}
